<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const isDownloading = ref(false);

onMounted(() => {
  if (attachment.value?.dataUrl) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

// Enquanto a foto carrega, a moldura já ocupa o tamanho dela (24/10/2026): sem isso a bolha
// nascia vazia e crescia de uma vez, empurrando a conversa para baixo. A moldura é uma
// `<img>` transparente, e não um `<div>`: só um elemento substituído encolhe junto com a
// bolha no celular (num `<div>` de 280px a bolha estourava 8px e a foto chegava menor).
// A proporção vem do anexo (em produção, 300 de 300 fotos têm a medida — 24/10/2026); sem
// `aspect-ratio` explícito o pixel 1×1 tornaria a moldura quadrada ao carregar.
const proporcao = computed(() => {
  const { width, height } = attachment.value || {};
  return { aspectRatio: width && height ? `${width} / ${height}` : '7 / 4' };
});

const PIXEL_VAZIO =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const handleImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <!-- Como no WhatsApp (Victor, 17/09/2026): moldura fina e a foto em
       tamanho de miniatura; o clique abre a imagem inteira em tela cheia. -->
  <BaseBubble
    class="overflow-hidden p-1"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg">
      <Icon icon="i-ph-prohibit" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div
      v-else-if="isLoaded"
      key="foto"
      class="relative group rounded-lg overflow-hidden msg-foto"
    >
      <img
        class="skip-context-menu msg-foto__img"
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button xs solid slate icon="i-ph-arrows-out" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-ph-download-simple"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
    <img
      v-else
      key="moldura"
      class="rounded-lg msg-foto__img msg-foto--carregando"
      :src="PIXEL_VAZIO"
      :style="proporcao"
      alt=""
      aria-hidden="true"
    />
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>

<style scoped>
/* A miniatura: até 280px de largura e 320px de altura. Foto muito alta é
   recortada pelo centro (só aqui — a galeria mostra a imagem inteira). */
.msg-foto {
  cursor: zoom-in;
}

/* Largura FIXA com teto, não `min(280px, 100%)`: dentro da bolha, que se ajusta ao
   conteúdo, a porcentagem não tem base, e a foto ainda sem pintar caía para 136×91 antes
   de voltar a 280×187 — um pulo depois da moldura (medido em 24/10/2026). */
.msg-foto__img {
  display: block;
  width: 280px;
  max-width: 100%;
  height: auto;
  max-height: 320px;
  object-fit: cover;
  animation: msg-foto-chega var(--mov-3) var(--mov-curva);
}

.msg-foto--carregando {
  background: rgb(var(--slate-3));
  cursor: default;
  animation: none;
}

@keyframes msg-foto-chega {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .msg-foto__img {
    animation: none;
  }
}
</style>
